import { useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getYakurekiDetail } from '@/shared/apis/yakureki'
import {
  isFailureStatus,
  type YakurekiDetail,
} from '@/shared/types/yakurekiCore'
import { isNotFoundError } from '@/shared/utils/errorHandling'

// L1 (Sync): YakurekiDetail をサーバから同期する唯一の入口. page が 1 instance だけ持ち、
// 得た event を useRecordingStatus (L3) に渡す (types.ts の 3-layer 宣言どおり).
// 子 component (MeetingBlock 等) は自前で query を張らず recordingStatus.event を読む
// ことで、同じ event を 2 つの observer が別 cadence で観測する状態を作らない.
//
// 2 秒間隔は旧UI (apps/legacy) の patient detail poll と同 cadence. 録音中の sequential
// update は 15 秒境界で走るため多くの poll は no-op だが、 SOAP 生成完了 (status=completed
// は SOAP 保存直後に書かれる) から UI 反映までの最大遅延を 2 秒以内に抑える.
const DEFAULT_INTERVAL_MS = 2_000

// 在宅の訪問報告書は status=completed (SOAP 保存直後) より数十秒遅れて書き込まれる
// (back は SOAP 保存後の後処理で LLM 生成する)。status 終端で polling を止めると取り逃し、
// 報告書が空欄のまま固まるため、在宅のみ生成の着地まで polling を延長する。生成失敗は
// back が握りつぶす (Sentry 送り) ため、終端到達からの猶予上限で必ず打ち切る
// (復旧手段は既存の手動再生成)。
const HOME_VISIT_FIELDS_GRACE_MS = 180_000

// L1 query の exact queryKey。cache を直接読み書きする側 (= 楽観更新の
// getQueryData / setQueryData) は prefix ではなく exact key を要求するため、
// key の知識はこの module から配る。
// (`queryKeys.yakureki.detail(id)` は invalidate/cancel の prefix としては効くが、
//  exact key ではないので getQueryData/setQueryData には使えない。)
export const yakurekiPollKey = (yakurekiId: string) =>
  ['yakureki', yakurekiId, 'poll'] as const

interface Options {
  yakurekiId: string | null
  enabled?: boolean
  intervalMs?: number
}

// サーバ側で何かが進行中の status. これらの間は 2 秒間隔で追う.
// pending (未録音) は待つ対象が無いので回さない: 録音開始は client 起点なので、
// useRecordingStatus が engine の start 遷移で invalidate して polling を起こす
// (back は stream/start と同じトランザクションで status=RECORDING を書くため、
//  start 応答後の refetch は必ず recording を観測する).
const isServerInFlight = (data: YakurekiDetail): boolean =>
  data.status === 'recording' || data.status === 'processing'

// 在宅で生成が未着なら true (= status 終端後も polling 継続の対象)。
//
// 判定は back が明示する home_visit_generated_at だけを見る。以前は home_visit_fields の
// キーの有無から「生成済み」を推測していたが、薬剤師が空欄期間に 1 行編集した瞬間に
// 報告書ができてキーが埋まるため、生成の着地を待たずに polling が止まっていた
// (= 後から届く生成結果が画面に出ない)。**状態は形から推測せず、サーバに言わせる。**
//
// SOAP 生成自体が失敗した event は後処理に到達せず生成が永久に来ないため待たない。
const isHomeVisitFieldsPending = (data: YakurekiDetail): boolean => {
  if (data.session_type !== 'home_visit') return false
  if (isFailureStatus(data.status)) return false
  return !data.soaps?.home_visit_generated_at
}

// 在宅の報告書を作り直している間 (資料の「反映する」の後など) は true。status は completed のまま
// 動かないので、上の猶予とは別に、back が言う「作り直し中」だけを見て追う。着地しない作り直しは
// back が時間で打ち切る (作り直し中と言い続けない) ので、ここでは上限を持たない。
const isHomeVisitReportRegenerating = (data: YakurekiDetail): boolean =>
  data.session_type === 'home_visit' &&
  !isFailureStatus(data.status) &&
  data.soaps?.home_visit_generating === true

// refetchInterval callback の判定本体。時刻を注入して単体テスト可能にした純関数。
// settledAt = 「進行中 → 終端」の遷移をこの session で観測した時刻 (未観測は null)。
// 未観測 (= 開いた時点で既に終端) では猶予を発動しない: 本機能の実装前に作られた在宅
// イベントは home_visit_fields が永久に null なので、サイドバーから開くたびに猶予いっぱい
// (3 分 × 2 秒 = 90 リクエスト) 空振りしてしまう。
export const resolvePollInterval = ({
  data,
  settledAt,
  now,
  intervalMs,
  isGone = false,
}: {
  data: YakurekiDetail | undefined
  settledAt: number | null
  now: number
  intervalMs: number
  /** 対象 event が消えている (404)。破棄で削除されたフリー録音がこれに当たる。 */
  isGone?: boolean
}): number | false => {
  // 消えた event は終端。待っても戻ってこないので回さない (ADR-0039)。
  if (isGone) return false
  // 初回 fetch 前は status 不明なので回す。
  if (!data) return intervalMs
  if (isServerInFlight(data)) return intervalMs
  if (isHomeVisitReportRegenerating(data)) return intervalMs
  if (
    isHomeVisitFieldsPending(data) &&
    settledAt !== null &&
    now - settledAt < HOME_VISIT_FIELDS_GRACE_MS
  ) {
    return intervalMs
  }
  return false
}

export const usePollEvent = ({
  yakurekiId,
  enabled = true,
  intervalMs = DEFAULT_INTERVAL_MS,
}: Options) => {
  // 「この session で進行中を観測したか」と「終端に落ちた時刻」。event が切り替わったら
  // 両方リセットする (id ごとに独立した観測にする)。
  const observedIdRef = useRef<string | null>(null)
  const inFlightSeenRef = useRef(false)
  const settledAtRef = useRef<number | null>(null)

  const query = useQuery({
    queryKey: yakurekiPollKey(yakurekiId ?? ''),
    queryFn: async () => {
      const res = await getYakurekiDetail(yakurekiId!)
      return res.data
    },
    enabled: enabled && !!yakurekiId,
    // 404 は終端 (= 破棄で削除されたフリー録音)。retry しても戻らないので即あきらめる。
    // それ以外 (5xx / network) は queryClient 既定と同じ 1 回だけ retry する。
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 1,
    refetchInterval: (query) => {
      const data = query.state.data
      const now = Date.now()
      if (data) {
        if (observedIdRef.current !== data.id) {
          observedIdRef.current = data.id
          inFlightSeenRef.current = false
          settledAtRef.current = null
        }
        if (isServerInFlight(data)) {
          inFlightSeenRef.current = true
          settledAtRef.current = null
        } else if (inFlightSeenRef.current) {
          settledAtRef.current ??= now
        }
      }
      return resolvePollInterval({
        data,
        settledAt: settledAtRef.current,
        now,
        intervalMs,
        isGone: isNotFoundError(query.state.error),
      })
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
  })

  // 露出は使う分だけ (query をそのまま spread すると tanstack の
  // no-rest-destructuring に触れ、 全 field の変化を観測して再 render が増える)。
  // isGone = 「この event はもう無い」。破棄でフリー録音の器が削除された後 (ADR-0039)、
  // 詳細画面はこれを見て一覧へ戻る (= 404 を叩き続けない)。
  return {
    data: query.data,
    isGone: isNotFoundError(query.error),
  }
}
